import {
  Avatar,
  Menu,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { CalendarDays, Download, Eye, User } from 'lucide-react';
import { formatCurrency, formatDateToBrazil } from '../../../shared/utils';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import { PolicyStatus } from '../../../shared/enums/policy-status.enum';
import type { PolicySummary } from '../../../services/policies/types';
import type { BrokerWriteGuard } from '../../brokers/utils/broker-status.util';

type TagColorScheme = 'gray' | 'success' | 'error';

const policyStatusConfig: Record<
  number,
  { label: string; colorScheme: TagColorScheme }
> = {
  [PolicyStatus.Effective]: { label: 'Emitida', colorScheme: 'success' },
  [PolicyStatus.Rejected]: { label: 'Cancelada', colorScheme: 'error' },
};

export type PolicyAction = 'details' | 'download-pdf';

export function createPoliciesColumns(
  // eslint-disable-next-line no-unused-vars
  onAction: (_action: PolicyAction, _policy: PolicySummary) => void,
  /** Corretora inativa: o PDF da apólice fica travado (a Api barra com 403). */
  downloadGuard?: BrokerWriteGuard
): DataTableColumn<PolicySummary>[] {
  return [
    {
      accessorKey: 'insuredName',
      header: 'SEGURADO',
      size: 190,
      cell: ({ row }) => {
        const policy = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
              <User className='w-4 h-4 text-white' />
            </Avatar.Root>
            <div className='flex flex-col gap-1'>
              <span className='font-gotham font-bold text-alba-black-500'>
                {policy.insuredName}
              </span>
              {policy.insuredDocument && (
                <span className='text-alba-gray-600 text-xs'>
                  {formatRegisterNumber(policy.insuredDocument)}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'brokerName',
      header: 'CORRETOR',
      size: 160,
      cell: ({ row }) => {
        const policy = row.original;
        const brokerName = policy.brokerName
          ? policy.brokerName.split('\n')[0]
          : '-';
        return (
          <span className='font-gotham font-bold text-alba-black-500 text-sm'>
            {brokerName}
          </span>
        );
      },
    },
    {
      accessorKey: 'productName',
      header: 'PRODUTO',
      size: 150,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.productName}
        </span>
      ),
    },
    {
      accessorKey: 'externalPolicyNo',
      header: 'Nº DA APÓLICE',
      size: 160,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm font-medium'>
          {row.original.externalPolicyNo || row.original.policyNo}
        </span>
      ),
    },
    {
      accessorKey: 'effectiveDate',
      header: 'DATA DE VIGÊNCIA',
      size: 160,
      cell: ({ row }) => {
        const policy = row.original;
        return (
          <div className='flex items-start gap-1.5'>
            <CalendarDays className='w-4 h-4 text-alba-gray-600 mt-0.5 shrink-0' />
            <div className='flex flex-col text-alba-gray-700 text-sm'>
              <span>{formatDateToBrazil(policy.effectiveDate)} -</span>
              {policy.expiryDate && (
                <span>{formatDateToBrazil(policy.expiryDate)}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'duePremium',
      header: 'PRÊMIO',
      size: 130,
      sortable: false,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm font-medium tabular-nums'>
          {row.original.duePremium
            ? formatCurrency(row.original.duePremium)
            : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'inclusionUserName',
      header: 'CRIADO POR',
      size: 150,
      sortable: false,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.inclusionUserName || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'policyStatus',
      header: 'STATUS',
      size: 130,
      sortable: false,
      cell: ({ row }) => {
        const policy = row.original;
        const statusNum = Number(policy.policyStatus);
        const config = policyStatusConfig[statusNum] ?? {
          label: 'Desconhecido',
          colorScheme: 'gray' as const,
        };

        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const policy = row.original;

        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                <Menu.Item
                  value='details'
                  icon={<Eye size={16} />}
                  onSelect={() => onAction('details', policy)}
                >
                  Ver detalhes
                </Menu.Item>
                <Menu.Item
                  value='download-pdf'
                  icon={<Download size={16} />}
                  disabled={downloadGuard?.blocked}
                  title={downloadGuard?.reason}
                  onSelect={() => onAction('download-pdf', policy)}
                >
                  Baixar PDF Apólice
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}
